import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../shared/stores/toast.store';
import { describeApiError } from '../../shared/api/errors';
import { ColaboradorSelect } from '../../shared/components/ColaboradorSelect';
import { formatDate } from '../../shared/reports/formatters';
import {
  useLinkProjetoColaborador,
  useProjetoColaboradores,
  useUnlinkProjetoColaborador,
  useUpdateProjetoColaborador,
} from './colaboradores.api';
import type { ProjetoColaboradorVinculado } from './types';

export interface ProjectColaboradoresDialogProps {
  open: boolean;
  clientId: number;
  projectId: number;
  projectNome: string;
  onClose: () => void;
}

function hoje(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * CRUD do vínculo Colaborador↔Projeto (transaction `ColaboradorClienteProjeto`)
 * a partir do projeto — espelha o WW `ClienteProjetoColabradorManutencaoView`
 * (inserir + editar datas + excluir). Cards [1pns0M7e] (List/POST/DELETE, real)
 * e [IP1Q7XXf] (PUT editar — mock até publicar). `dataInicio` obrigatório
 * (informado pelo usuário); `dataTermino` opcional (≥ início).
 */
export function ProjectColaboradoresDialog({
  open,
  clientId,
  projectId,
  projectNome,
  onClose,
}: ProjectColaboradoresDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();

  const vinculadosQuery = useProjetoColaboradores(clientId, open ? projectId : null);
  const link = useLinkProjetoColaborador(clientId, projectId);
  const unlink = useUnlinkProjetoColaborador(clientId, projectId);
  const update = useUpdateProjetoColaborador(clientId, projectId);

  const [selecionado, setSelecionado] = useState<number | null>(null);
  const [editando, setEditando] = useState<ProjetoColaboradorVinculado | null>(null);
  const [dataInicio, setDataInicio] = useState<string>(hoje());
  const [dataTermino, setDataTermino] = useState<string>('');

  const vinculados = vinculadosQuery.data ?? [];
  const jaVinculado =
    selecionado !== null && vinculados.some((v) => v.colaboradorCodigo === selecionado);
  const terminoInvalido = dataTermino !== '' && dataTermino < dataInicio;
  const isBusy = link.isPending || unlink.isPending || update.isPending;
  const datasOk = dataInicio !== '' && !terminoInvalido;
  const podeAdicionar = selecionado !== null && !jaVinculado && datasOk && !isBusy;

  function resetForm(): void {
    setSelecionado(null);
    setEditando(null);
    setDataInicio(hoje());
    setDataTermino('');
  }

  function startEdit(v: ProjetoColaboradorVinculado): void {
    setEditando(v);
    setSelecionado(null);
    setDataInicio(v.dataInicio);
    setDataTermino(v.dataTermino ?? '');
  }

  async function handleAdd(): Promise<void> {
    if (selecionado === null || !datasOk) return;
    try {
      await link.mutateAsync({
        colaboradorCodigo: selecionado,
        dataInicio,
        dataTermino: dataTermino || null,
      });
      resetForm();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function handleSaveEdit(): Promise<void> {
    if (editando === null || !datasOk) return;
    try {
      await update.mutateAsync({
        colaboradorCodigo: editando.colaboradorCodigo,
        dataInicio,
        dataTermino: dataTermino || null,
      });
      toast.success(t('features.clientes.projetosGlobal.colaboradores.editSalvo'));
      resetForm();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function handleRemove(codigo: number): Promise<void> {
    try {
      await unlink.mutateAsync(codigo);
      if (editando?.colaboradorCodigo === codigo) resetForm();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: resetForm }}
    >
      <DialogTitle>
        {t('features.clientes.projetosGlobal.colaboradores.title', { projeto: projectNome })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('features.clientes.projetosGlobal.colaboradores.descricao')}
          </Typography>

          {/* Formulário: alterna entre Adicionar (novo) e Editar (existente). */}
          <Stack spacing={1.5}>
            {editando ? (
              <Typography variant="subtitle2">
                {t('features.clientes.projetosGlobal.colaboradores.editandoLabel', {
                  nome: editando.colaboradorNome,
                })}
              </Typography>
            ) : (
              <ColaboradorSelect
                value={selecionado}
                onChange={(id) => setSelecionado(id)}
                label={t('features.clientes.projetosGlobal.colaboradores.adicionarLabel')}
                error={jaVinculado}
                helperText={
                  jaVinculado
                    ? t('features.clientes.projetosGlobal.colaboradores.jaVinculado')
                    : undefined
                }
              />
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
              <TextField
                type="date"
                size="small"
                required
                fullWidth
                label={t('features.clientes.projetosGlobal.colaboradores.dataInicio')}
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                size="small"
                fullWidth
                label={t('features.clientes.projetosGlobal.colaboradores.dataTermino')}
                value={dataTermino}
                onChange={(e) => setDataTermino(e.target.value)}
                error={terminoInvalido}
                helperText={
                  terminoInvalido
                    ? t('features.clientes.projetosGlobal.colaboradores.terminoInvalido')
                    : t('features.clientes.projetosGlobal.colaboradores.dataTerminoHelper')
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: dataInicio || undefined }}
              />
              {editando ? (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => void handleSaveEdit()}
                    disabled={!datasOk || isBusy}
                  >
                    {t('common.save')}
                  </Button>
                  <Button onClick={resetForm} disabled={isBusy}>
                    {t('common.cancel')}
                  </Button>
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => void handleAdd()}
                  disabled={!podeAdicionar}
                  sx={{ mt: 0.5, flexShrink: 0 }}
                >
                  {t('features.clientes.projetosGlobal.colaboradores.adicionar')}
                </Button>
              )}
            </Stack>
          </Stack>

          {vinculadosQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : vinculados.length === 0 ? (
            <Alert severity="info" variant="outlined">
              {t('features.clientes.projetosGlobal.colaboradores.vazio')}
            </Alert>
          ) : (
            <List dense disablePadding>
              {vinculados.map((v) => (
                <ListItem
                  key={v.colaboradorCodigo}
                  divider
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('features.clientes.projetosGlobal.colaboradores.editar')}>
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            disabled={isBusy}
                            aria-label={t(
                              'features.clientes.projetosGlobal.colaboradores.editarAria',
                              {
                                nome: v.colaboradorNome,
                              },
                            )}
                            onClick={() => startEdit(v)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('features.clientes.projetosGlobal.colaboradores.remover')}>
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            disabled={isBusy}
                            aria-label={t(
                              'features.clientes.projetosGlobal.colaboradores.removerAria',
                              {
                                nome: v.colaboradorNome,
                              },
                            )}
                            onClick={() => void handleRemove(v.colaboradorCodigo)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={v.colaboradorNome}
                    secondary={
                      v.dataTermino
                        ? t('features.clientes.projetosGlobal.colaboradores.periodo', {
                            inicio: formatDate(v.dataInicio),
                            termino: formatDate(v.dataTermino),
                          })
                        : t('features.clientes.projetosGlobal.colaboradores.desde', {
                            data: formatDate(v.dataInicio),
                          })
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
